import { Button, Divider, Drawer, Stack, FormLabel } from "@mui/material";
import { useStore } from "@tanstack/react-form";
import { useMemo } from "react";

import { withForm } from "components/Forms/AppForm";
import { groupByFirstLetter } from "components/Forms/components/fields/select/utils";
import { standardDateFormat } from "utils/timeUtils";
import { isString } from "utils/typeGuards";

import { ppFilterDefaultValues } from "../../context/ppFilterDefaultValues";
import { useFilteredPps } from "../../hooks/useFilteredPps";

import PpStatusOptions from "./PpStatusOptions";

import "components/Products/Products.css";

const PpFilter = withForm({
  defaultValues: ppFilterDefaultValues,
  props: {
    show: false,
  },
  render: function Render({ form, show }) {
    const filters = useStore(form.store, (state) => state.values);
    const { protectionProfiles } = useFilteredPps(filters);

    const options = useMemo(() => {
      if (!protectionProfiles.length) {
        return {
          techTypes: [] as string[],
          ccVersions: [] as string[],
          sunsetDates: [] as string[],
        };
      }

      const techTypes = [
        ...new Set(
          protectionProfiles
            .map((pp) => pp.tech_type)
            .filter((techType) => isString(techType)),
        ),
      ]
        .filter(Boolean)
        .sort();

      const ccVersions = [
        ...new Set(
          protectionProfiles
            .map((pp) => pp.cc_version)
            .filter((techType) => isString(techType)),
        ),
      ]
        .filter(Boolean)
        .sort();

      const sunsetDates = [
        ...new Set(protectionProfiles.map((pp) => pp.sunset_date)),
      ]
        .filter(Boolean)
        .sort()
        .map((date) => ({
          label: standardDateFormat(date),
          value: date as string,
        }));

      return {
        techTypes,
        ccVersions,
        sunsetDates,
      };
    }, [protectionProfiles]);

    const resetPpFilter = (): void => {
      form.reset();
    };

    return (
      <Drawer
        open={show}
        variant="persistent"
        sx={{
          position: "sticky",
          top: 0,
          height: "fit-content",
          width: show ? "250px" : 0,
          ".MuiDrawer-paperAnchorLeft": {
            position: "static",
            top: 0,
            marginRight: "1em",
            marginBottom: "1em",
            paddingTop: "0",
            marginTop: "1em",
            flex: 0,
            paddingRight: "1em",
            overflowY: "visible",
          },
        }}
      >
        <form>
          <form.AppForm>
            <form.AppField name="search">
              {(field) => <field.SearchBar captureSearch={true} />}
            </form.AppField>

            <Stack gap={1.25} marginTop={3}>
              <FormLabel>Filter Options</FormLabel>

              <form.AppField name="tech_type">
                {(field) => (
                  <field.AutocompleteSelect
                    options={options.techTypes}
                    label="Technology Type"
                    groupBy={groupByFirstLetter}
                  />
                )}
              </form.AppField>

              <form.AppField name="cc_version">
                {(field) => (
                  <field.AutocompleteSelect
                    options={options.ccVersions}
                    label="CC Version"
                  />
                )}
              </form.AppField>

              <form.AppField name="sunset_date">
                {(field) => (
                  <field.AutocompleteSelect
                    options={options.sunsetDates}
                    label="Sunset Date"
                  />
                )}
              </form.AppField>
            </Stack>

            <PpStatusOptions form={form} />

            <Divider
              sx={{ opacity: 1, borderColor: "rgba(0,0,0,.15)", margin: 2 }}
            />

            <Button
              fullWidth
              onClick={resetPpFilter}
              sx={{
                backgroundColor: "#212529",
                color: "white",
                "&:hover": {
                  background: "#424649",
                },
              }}
            >
              Clear Filters
            </Button>
          </form.AppForm>
        </form>
      </Drawer>
    );
  },
});

export default PpFilter;
